import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Alert,
  LinearProgress,
  Typography,
  Card,
  CardContent,
  Paper,
  Chip,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Sync as SyncIcon,
  Preview as PreviewIcon,
  AccountBalance as OdooIcon,
  CheckCircle as OkIcon,
  Cancel as BadIcon
} from '@mui/icons-material';
import logger from '../../utils/logger';

const TARGETS = [
  { key: 'customers', label: 'Customers → Odoo contacts' },
  { key: 'maids', label: 'Technicians → Odoo vendors (1099)' },
  { key: 'services', label: 'Service catalog → Odoo products' },
  { key: 'invoices', label: 'Completed jobs → draft customer invoices (AR)' },
  { key: 'payments', label: 'Stripe payments → registered on posted invoices' },
  { key: 'payables', label: 'Technician payouts → vendor bills (AP)' },
  { key: 'mailing', label: 'Customers & leads → Odoo mailing lists' }
];

const OdooSyncDialog = ({ open, onClose }) => {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [isPreview, setIsPreview] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [targets, setTargets] = useState(['customers', 'maids', 'services', 'invoices', 'payments', 'payables', 'mailing']);

  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '') || '';

  const fetchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const response = await fetch(`${apiBase}/api/admin/odoo/status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      setStatus(data.status || null);
    } catch (err) {
      logger.log('[OdooSyncDialog] Status fetch failed:', err.message);
      setStatus(null);
    } finally {
      setStatusLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    if (open) {
      fetchStatus();
      setResults(null);
      setError('');
      setSuccess('');
    }
  }, [open, fetchStatus]);

  const toggleTarget = (key) => {
    setTargets((prev) =>
      prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key]
    );
  };

  const runSync = async (dryRun) => {
    setError('');
    setSuccess('');
    setResults(null);
    setIsPreview(dryRun);
    if (dryRun) setPreviewLoading(true);
    else setLoading(true);

    try {
      const response = await fetch(`${apiBase}/api/admin/odoo/sync`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ targets, dryRun })
      });

      const data = await response.json();
      if (!response.ok || !data.success) throw new Error(data.error || 'Sync failed');

      setResults(data.results);
      if (!dryRun) {
        setSuccess(data.message);
        fetchStatus();
      }
      logger.log('[OdooSyncDialog] Sync results:', data.results);
    } catch (err) {
      setError(`${dryRun ? 'Preview' : 'Sync'} failed: ${err.message}`);
    } finally {
      setLoading(false);
      setPreviewLoading(false);
    }
  };

  const handleSync = () => {
    if (
      !window.confirm(
        'This will push the selected records into Odoo (creating or updating contacts, vendors and products). Continue?'
      )
    ) {
      return;
    }
    runSync(false);
  };

  const busy = loading || previewLoading;
  const connected = status?.configured && status?.reachable && status?.authenticated;

  const statusChip = (label, ok) => (
    <Chip
      icon={ok ? <OkIcon /> : <BadIcon />}
      label={label}
      color={ok ? 'success' : 'error'}
      size="small"
      variant="outlined"
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <OdooIcon color="primary" />
          Odoo Sync
        </Box>
      </DialogTitle>

      <DialogContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Alert severity="info">
              <Typography variant="body2">
                Pushes master data into the Odoo accounting back-office. Re-running is safe:
                unchanged records are skipped, changed ones are updated — never duplicated.
              </Typography>
            </Alert>
          </Grid>

          {/* Connection status */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="subtitle2" sx={{ mr: 1 }}>
                  Connection:
                </Typography>
                {statusLoading ? (
                  <Typography variant="body2" color="text.secondary">checking…</Typography>
                ) : status ? (
                  <>
                    {statusChip('Configured', status.configured)}
                    {statusChip('Reachable', status.reachable)}
                    {statusChip('Authenticated', status.authenticated)}
                    {status.serverVersion && (
                      <Chip label={`Odoo ${status.serverVersion}`} size="small" />
                    )}
                  </>
                ) : (
                  <Chip label="Status unavailable" color="warning" size="small" />
                )}
              </Box>
              {status?.syncStats && Object.keys(status.syncStats).length > 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Already synced:{' '}
                  {Object.entries(status.syncStats)
                    .map(([model, s]) => `${s.count} ${model === 'MaidProfile' ? 'Technician' : model}`)
                    .join(', ')}
                </Typography>
              )}
            </Paper>
          </Grid>

          {/* Target selection + actions */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                What to sync
              </Typography>
              <FormGroup>
                {TARGETS.map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    control={
                      <Checkbox
                        checked={targets.includes(key)}
                        onChange={() => toggleTarget(key)}
                        disabled={busy}
                      />
                    }
                    label={label}
                  />
                ))}
              </FormGroup>

              <Box display="flex" gap={1} mt={2}>
                <Button
                  variant="outlined"
                  startIcon={<PreviewIcon />}
                  onClick={() => runSync(true)}
                  disabled={busy || targets.length === 0 || !connected}
                  fullWidth
                >
                  Preview
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SyncIcon />}
                  onClick={handleSync}
                  disabled={busy || targets.length === 0 || !connected}
                  fullWidth
                >
                  Sync Now
                </Button>
              </Box>
              {!connected && !statusLoading && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  Odoo is not reachable — check the integration configuration.
                </Typography>
              )}
            </Paper>
          </Grid>

          {busy && (
            <Grid item xs={12}>
              <LinearProgress />
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                {loading ? 'Syncing to Odoo… this can take a few minutes.' : 'Computing preview…'}
              </Typography>
            </Grid>
          )}

          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          {success && (
            <Grid item xs={12}>
              <Alert severity="success">{success}</Alert>
            </Grid>
          )}

          {/* Results */}
          {results && (
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <Typography variant="h6">
                      {isPreview ? 'Preview Results' : 'Sync Results'}
                    </Typography>
                    {isPreview && <Chip label="dry run — nothing written" color="info" size="small" />}
                  </Box>

                  {TARGETS.filter(({ key }) => results[key]).map(({ key, label }) => {
                    const r = results[key];
                    return (
                      <Box key={key} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {label}
                        </Typography>
                        <Grid container spacing={2}>
                          {[
                            { l: 'Total', v: r.total, c: 'primary' },
                            { l: isPreview ? 'Would create' : 'Created', v: r.created, c: 'success.main' },
                            { l: isPreview ? 'Would update' : 'Updated', v: r.updated, c: 'info.main' },
                            { l: 'Unchanged', v: r.skipped, c: 'text.secondary' },
                            { l: 'Errors', v: r.errors, c: 'error.main' }
                          ].map(({ l, v, c }) => (
                            <Grid item xs={4} sm={2.4} key={l}>
                              <Typography variant="body2" color="text.secondary">{l}</Typography>
                              <Typography variant="h5" color={c}>{v ?? 0}</Typography>
                            </Grid>
                          ))}
                        </Grid>
                        <Divider sx={{ mt: 1 }} />
                      </Box>
                    );
                  })}

                  {typeof results.durationMs === 'number' && (
                    <Typography variant="body2" color="text.secondary">
                      Completed in {(results.durationMs / 1000).toFixed(1)}s
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default OdooSyncDialog;
